import React, { useEffect, useState } from "react";
import {
  ChevronDown,
  ChevronUp,
  History,
  Loader2,
  Trash2,
} from "lucide-react";

const scoreColumns = [
  { label: "Content", key: "contentScore" },
  { label: "Pronunciation", key: "pronounctionScore" },
  { label: "Fluency", key: "fluencyScore" },
  { label: "Total", key: "totalScore" },
];

const contentOnlyScoreColumns = [
  { label: "Content", key: "contentScore" },
  { label: "Total", key: "totalScore" },
];

const getScoreColumns = (response) =>
  response.fluencyScore == null && response.pronounctionScore == null
    ? contentOnlyScoreColumns
    : scoreColumns;

const normalizeHighlightText = (value) =>
  String(value || "")
    .toLowerCase()
    .replace(/&/g, " and ")
    .replace(/[^a-z0-9\s-]/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .split(" ")
    .filter((word) => word && !["a", "an", "the", "uh", "um", "umm"].includes(word))
    .join(" ");

const splitAcceptedHighlights = (answerText) =>
  String(answerText || "")
    .split(/\s*(?:,|;|\/|\||\bor\b)\s*/i)
    .map(normalizeHighlightText)
    .filter(Boolean);

const answerMatchesAcceptedKey = (candidateAnswer, acceptedAnswer) => {
  const candidate = normalizeHighlightText(candidateAnswer);
  const accepted = normalizeHighlightText(acceptedAnswer);
  if (!candidate || !accepted) return false;
  if (candidate === accepted) return true;

  const candidateWords = candidate.split(" ");
  const acceptedWords = accepted.split(" ");
  if (acceptedWords.length === 1) return candidateWords.includes(accepted);

  const candidateSet = new Set(candidateWords);
  const overlap = acceptedWords.filter((word) => candidateSet.has(word)).length;
  return candidate.includes(accepted) || overlap / acceptedWords.length >= 0.9;
};

const ScoreBox = ({
  originalWords,
  practiceItemId,
  onIsAttempted,
  showCorrectAnswers = false,
}) => {
  const [responses, setResponses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    const fetchResponses = async () => {
      try {
        const response = await fetch(
          `/api/get-user-responses/${practiceItemId}`
        );
        if (!response.ok) throw new Error("Failed to fetch responses");

        const responseData = await response.json();
        setResponses(responseData);

        if (responseData.length > 0) {
          onIsAttempted(responseData[0].isAttempted);
        }
      } catch (error) {
        console.error("Error fetching responses:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchResponses();
  }, [onIsAttempted, practiceItemId]);

  const deleteResponse = (id) => {
    const updatedResponses = responses.filter((response) => response.id !== id);
    setResponses(updatedResponses);
  };

  const highlightMatchingWords = (original, userResponse) => {
    const acceptedAnswers = splitAcceptedHighlights(original);
    const responseMatchesKey = acceptedAnswers.some((answer) =>
      answerMatchesAcceptedKey(userResponse, answer)
    );
    const originalTokenSet = new Set(normalizeHighlightText(original).split(" "));
    const words2 = userResponse.split(" ");

    return words2.map((word2, index) => (
      <span
        key={`${word2}-${index}`}
        className={`mb-1 mr-1 rounded-md px-2 py-1 text-xs font-semibold ${
          responseMatchesKey || originalTokenSet.has(normalizeHighlightText(word2))
            ? "bg-green-50 text-green-700 ring-1 ring-green-100"
            : "bg-red-50 text-red-700 ring-1 ring-red-100"
        }`}
      >
        {word2}
      </span>
    ));
  };

  return (
    <div className="rounded-md border border-slate-200 bg-white p-4 shadow-sm">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <span className="grid h-10 w-10 shrink-0 place-items-center rounded-md bg-purple-50 text-purple-700">
            <History className="h-5 w-5" aria-hidden="true" />
          </span>
          <div>
            <p className="text-sm font-semibold uppercase text-purple-700">
              History
            </p>
            <h2 className="text-lg font-extrabold text-gray-900">
              Your responses
            </h2>
          </div>
        </div>

        <button
          type="button"
          onClick={() => setIsOpen((current) => !current)}
          className="grid h-10 w-10 shrink-0 place-items-center rounded-md bg-slate-50 text-gray-500 transition hover:bg-purple-50 hover:text-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-100"
          aria-label={isOpen ? "Collapse responses" : "Expand responses"}
        >
          {isOpen ? (
            <ChevronUp className="h-5 w-5" aria-hidden="true" />
          ) : (
            <ChevronDown className="h-5 w-5" aria-hidden="true" />
          )}
        </button>
      </div>

      {isOpen && (
        <div className="mt-4">
          {loading && (
            <div className="flex min-h-24 items-center justify-center rounded-md bg-slate-50 text-sm font-semibold text-gray-500">
              <Loader2 className="mr-2 h-4 w-4 animate-spin text-purple-700" />
              Loading responses
            </div>
          )}

          {!loading && responses.length === 0 && (
            <div className="rounded-md bg-slate-50 p-4 text-center text-sm text-gray-500">
              No responses yet.
            </div>
          )}

          {!loading && responses.length > 0 && (
            <div className="space-y-4">
              {responses.map((response, index) => (
                <article
                  key={response.id || `${response.updatedAt}-${index}`}
                  className="rounded-md border border-slate-200 bg-slate-50 p-3"
                >
                  <div className="mb-3 flex items-center justify-between gap-3">
                    <div>
                      <p className="text-xs font-semibold uppercase text-gray-500">
                        Attempt {index + 1}
                      </p>
                      <p className="text-sm font-bold text-gray-900">
                        {new Date(response.updatedAt).toLocaleDateString(
                          "en-GB"
                        )}
                      </p>
                    </div>
                    <button
                      type="button"
                      className="grid h-9 w-9 place-items-center rounded-md bg-white text-gray-400 shadow-sm transition hover:bg-red-50 hover:text-red-600"
                      onClick={() => deleteResponse(response.id)}
                      aria-label="Delete response"
                    >
                      <Trash2 className="h-4 w-4" aria-hidden="true" />
                    </button>
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    {getScoreColumns(response).map((column) => (
                      <div
                        key={column.key}
                        className="rounded-md bg-white p-3 shadow-sm"
                      >
                        <p className="text-xs font-semibold uppercase text-gray-500">
                          {column.label}
                        </p>
                        <p className="mt-1 text-lg font-extrabold text-gray-900">
                          {response[column.key] ?? "N/A"}
                        </p>
                      </div>
                    ))}
                  </div>

                  <div className="mt-3 rounded-md bg-white p-3 shadow-sm">
                    <p className="text-xs font-semibold uppercase text-gray-500">
                      User response
                    </p>
                    <div className="mt-2 flex flex-wrap">
                      {highlightMatchingWords(
                        originalWords,
                        response.userResponse || "No detailed response yet."
                      )}
                    </div>
                  </div>

                  {showCorrectAnswers && originalWords && (
                    <div className="mt-3 rounded-md bg-green-50 p-3 shadow-sm ring-1 ring-green-100">
                      <p className="text-xs font-semibold uppercase text-green-700">
                        Correct answer
                      </p>
                      <p className="mt-1 text-sm font-extrabold text-green-900">
                        {originalWords}
                      </p>
                    </div>
                  )}

                  {response.remarks && (
                    <p className="mt-3 rounded-md bg-white p-3 text-sm font-semibold text-gray-700 shadow-sm">
                      {response.remarks}
                    </p>
                  )}
                </article>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ScoreBox;
